<script setup lang="ts">
/**
 * One household, and everything an organizer can do about it (SPEC §8.11, §6.5).
 *
 * Composed from pieces that already exist rather than rebuilt: the access
 * controls are the card from MT-069, the invitation flow the card from MT-071.
 * Both were built and tested with their own issues; this is where they get
 * mounted.
 *
 * Phone numbers are masked, always. An organizer needs to tell two contacts
 * apart and confirm they have the right one; neither needs the whole number,
 * and a screen that showed it would put it into every screenshot and support
 * ticket (SPEC §19.1).
 */
import { computed, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { ApiError, endpoints, type HouseholdContactResponse, type HouseholdResponse } from '@/api'
import HouseholdAccessCard from '@/components/HouseholdAccessCard.vue'
import HouseholdInviteCard from '@/components/HouseholdInviteCard.vue'

const route = useRoute()

const groupId = computed(() => route.params.groupId as string)
const householdId = computed(() => route.params.householdId as string)

const household = ref<HouseholdResponse | null>(null)
const contacts = ref<HouseholdContactResponse[]>([])
const loading = ref(true)
const error = ref<string | null>(null)

function describe(caught: unknown): string {
  return (caught instanceof ApiError && caught.problem?.title) || 'That did not work.'
}

async function load() {
  loading.value = true
  error.value = null

  try {
    household.value = await endpoints.getHousehold(groupId.value, householdId.value)
    contacts.value = await endpoints.listHouseholdContacts(groupId.value, householdId.value)
  } catch (caught) {
    error.value = describe(caught)
  } finally {
    loading.value = false
  }
}

watch([groupId, householdId], load, { immediate: true })
</script>

<template>
  <v-container class="household">
    <v-alert v-if="error" type="error" variant="tonal" class="mb-4">{{ error }}</v-alert>

    <v-skeleton-loader v-if="loading" type="article" />

    <template v-else-if="household">
      <header class="household__header">
        <div>
          <h1 class="household__title">{{ household.name }}</h1>
          <p class="household__counts">
            Usually {{ household.defaultAdultCount }}
            {{ household.defaultAdultCount === 1 ? 'adult' : 'adults' }} and
            {{ household.defaultChildCount }}
            {{ household.defaultChildCount === 1 ? 'kid' : 'kids' }}
          </p>
        </div>

        <v-chip v-if="household.status === 'Archived'" variant="tonal">Archived</v-chip>
      </header>

      <v-btn
        variant="text"
        :to="{ name: 'organizer-households', params: { groupId } }"
        class="mb-4"
      >
        Back to households
      </v-btn>

      <v-card v-if="household.foodNotes" variant="outlined" class="mb-6">
        <v-card-item>
          <v-card-title tag="h2" class="household__section">Food notes</v-card-title>
        </v-card-item>
        <v-card-text>{{ household.foodNotes }}</v-card-text>
      </v-card>

      <v-card variant="outlined" class="mb-6">
        <v-card-item>
          <v-card-title tag="h2" class="household__section">Who is connected</v-card-title>
          <v-card-subtitle class="household__section-note">
            Everybody here can answer for {{ household.name }}.
          </v-card-subtitle>
        </v-card-item>

        <v-card-text>
          <p v-if="contacts.length === 0" class="household__empty">
            Nobody has opened their link yet. Send them an invitation below.
          </p>

          <ul v-else class="household__contacts">
            <li v-for="contact in contacts" :key="contact.id" class="household__contact">
              <div>
                <strong>{{ contact.displayName ?? 'Someone' }}</strong>
                <!-- The last four digits, never the number (SPEC §19.1). -->
                <span class="household__phone">{{ contact.maskedPhone }}</span>
              </div>

              <div class="household__contact-flags">
                <v-chip v-if="contact.isPrimary" size="x-small" variant="tonal">Primary</v-chip>
                <v-chip
                  v-if="contact.needsReverification"
                  size="x-small"
                  variant="tonal"
                  color="warning"
                >
                  Needs confirming
                </v-chip>
                <v-chip
                  v-if="contact.smsState !== 'Allowed'"
                  size="x-small"
                  variant="tonal"
                  color="warning"
                >
                  {{ contact.smsState === 'OptedOut' ? 'Asked to stop' : 'Not reachable' }}
                </v-chip>
              </div>
            </li>
          </ul>
        </v-card-text>
      </v-card>

      <HouseholdInviteCard
        :group-id="groupId"
        :household-id="householdId"
        :household-name="household.name"
        class="mb-6"
      />

      <HouseholdAccessCard
        :group-id="groupId"
        :household-id="householdId"
        :household-name="household.name"
        class="mb-6"
      />

      <!-- Meals arrive in Phase 05. Named rather than absent, so the shape of
           this screen does not change under somebody when they do. -->
      <v-card variant="outlined">
        <v-card-item>
          <v-card-title tag="h2" class="household__section">Meals</v-card-title>
        </v-card-item>
        <v-card-text>
          <p class="household__empty">
            What {{ household.name }} are coming to, and what they brought last time, appears here
            once meals are scheduled.
          </p>
        </v-card-text>
      </v-card>
    </template>
  </v-container>
</template>

<style scoped>
.household__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
}

.household__title {
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0;
}

.household__counts {
  margin: 0.25rem 0 0;
}

.household__section {
  font-size: 1.125rem;
  font-weight: 600;
}

.household__section-note {
  white-space: normal;
  opacity: 1;
}

.household__contacts {
  list-style: none;
  margin: 0;
  padding: 0;
}

.household__contact {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  padding: 0.5rem 0;
}

.household__contact + .household__contact {
  border-top: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}

.household__phone {
  margin-left: 0.5rem;
}

.household__contact-flags {
  display: flex;
  gap: 0.25rem;
  flex-wrap: wrap;
}

.household__empty {
  margin: 0;
  max-width: 52ch;
}
</style>
